// client/src/pages/UserReports/UserReports.js
// Symptomrapporterings-sida. Submitar rapport och visar matchningar på
// postal/stad/land-skala efteråt med QuarterlyTrendChart per nivå.
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { publicApi } from '../../services/api/apiService';
import PostalCodeInput from '../../components/shared/form/PostalCodeInput';
import postalCodeService from '../../services/postalCodeService';
import API_CONFIG from '../../config/apiConfig';
import { fetchPostalGroups } from '../../services/postalGroupsService';
import QuarterlyTrendChart from '../../components/shared/health/QuarterlyTrendChart';
import { ALL_SYMPTOMS, getSymptomColor } from '../../components/shared/constants/symptoms';
import './UserReports.css';

const UserReports = () => {
  const { t } = useTranslation();
  
  // Helper: translate symptom names (Swedish keys → localized display)
  const tSymptom = (name) => t(`symptoms.${name}`, name);
  
  const [selectedWeek, setSelectedWeek] = useState(53);
  const [reportData, setReportData] = useState({
    postalCode: '',
    city: '',
    symptoms: [],
    severity: 'medium',
    notes: ''
  });
  
  const [postalCodeSuggestions, setPostalCodeSuggestions] = useState([]);
  const [searchResults, setSearchResults] = useState({
    postal: { matches: [], totalReports: 0 },
    city: { matches: [], totalReports: 0 },
    country: { matches: [], totalReports: 0 }
  });
  
  const [cityTrend, setCityTrend] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [postalCodeValid, setPostalCodeValid] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(''); // ✅ Will be set from backend
  
  const [detailedInsights, setDetailedInsights] = useState({
    growing: null,
    popular: null,
    recovery: null,
    loading: false,
    error: null
  });

  // ✅ REMOVED: availableSymptoms useMemo - now imported from shared/constants/symptoms
  // ✅ REMOVED: getSymptomColor function - now imported from shared/constants/symptoms

  const normalizePostalCode = (code) => {
    return code.replace(/\s+/g, '').toUpperCase();
  };

  const isSymptomSelected = (symptom) => {
    const symptomName = typeof symptom === 'string' ? symptom : symptom.name || symptom._id;
    return reportData.symptoms.some(s => {
      const selectedName = typeof s === 'string' ? s : s.name || s._id;
      return selectedName === symptomName;
    });
  };

  const handlePostalCodeChange = (value) => {
    const suggestions = postalCodeService.searchPostalCodes(value, 5);
    setPostalCodeSuggestions(suggestions);
    setReportData(prev => ({ ...prev, postalCode: value, city: '' }));
  };
  
  const handlePostalCodeSelect = (suggestion) => {
    setReportData(prev => ({
      ...prev,
      postalCode: suggestion.code,
      city: suggestion.city || ''
    }));
    setPostalCodeSuggestions([]);
  };
  
  // ✅ UPDATED: Enhanced postal code validation with async API lookup
  const handlePostalCodeValidation = async (isValid) => {
    setPostalCodeValid(isValid);
    
    if (isValid && reportData.postalCode && !reportData.city) {
      const normalizedCode = reportData.postalCode.replace(/\s+/g, '');
      
      // Step 1: Try static lookup first (instant)
      const suggestions = postalCodeService.searchPostalCodes(normalizedCode, 1);
      if (suggestions && suggestions.length > 0 && suggestions[0].city) {
        setReportData(prev => ({ ...prev, city: suggestions[0].city }));
        return;
      }
      
      // Step 2: API lookup from diseaseReports (async)
      try {
        const response = await fetch(`${API_CONFIG.baseUrl}/disease-reports/postal-lookup/${normalizedCode}`);
        const result = await response.json();
        
        if (result.success && result.city) {
          console.log(`📮 API lookup: ${normalizedCode} → ${result.city}`);
          setReportData(prev => ({ ...prev, city: result.city }));
          
          // Auto-learn: Save to local cache for future
          postalCodeService.addPostalCode(normalizedCode, result.city);
        }
      } catch (error) {
        console.log(`📮 API lookup failed for ${normalizedCode}:`, error.message);
        // Silent fail - city stays empty, user can fill manually
      }
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReportData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSymptomToggle = (symptom) => {
    const symptomName = typeof symptom === 'string' ? symptom : symptom.name || symptom._id;
    setReportData(prev => {
      const symptomExists = prev.symptoms.some(s => {
        const existingName = typeof s === 'string' ? s : s.name || s._id;
        return existingName === symptomName;
      });
      
      if (symptomExists) {
        return {
          ...prev,
          symptoms: prev.symptoms.filter(s => {
            const existingName = typeof s === 'string' ? s : s.name || s._id;
            return existingName !== symptomName;
          })
        };
      } else {
        return {
          ...prev,
          symptoms: [...prev.symptoms, symptomName]
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reportData.symptoms.length || !reportData.postalCode || !postalCodeValid) {
      setMessage({ type: 'error', text: t('symptomCheck.validation.requiredFields') });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const submissionData = {
        symptoms: reportData.symptoms,
        postalCode: normalizePostalCode(reportData.postalCode),
        city: reportData.city,
        severity: reportData.severity,
        notes: reportData.notes,
        timestamp: new Date().toISOString(),
        week: selectedWeek
      };

      const result = await publicApi.submitSymptomReport(submissionData);
         
      if (result.success) {
        setSubmittedData(submissionData);
        setSubmitSuccess(true);
        setShowResults(true);

        // ✅ NEW: Auto-learn postal code → city mapping after successful submit
        if (submissionData.postalCode && submissionData.city && 
            submissionData.city !== 'Unknown City' && submissionData.city.trim() !== '') {
          const learned = postalCodeService.addPostalCode(submissionData.postalCode, submissionData.city);
          if (learned) {
            console.log(`🎯 Auto-learned: ${submissionData.postalCode} → ${submissionData.city}`);
          }
        }
        
        setTimeout(() => {
          searchSimilarReports(submissionData);
          fetchDetailedInsights(submissionData);
        }, 300);
      } else {
        setMessage({ type: 'error', text: result.message || t('symptomCheck.validation.errorGeneric') });
      }
    } catch (error) {
      console.error('❌ Error submitting report:', error);
      setMessage({ type: 'error', text: t('symptomCheck.validation.errorGeneric') });
    } finally {
      setLoading(false);
    }
  };

  const processLevelMatches = (apiData, level) => {
    if (!apiData?.success || !apiData.data || apiData.data.length === 0) {
      return [];
    }

    if (level === 'postal') {
      return apiData.data.map(group => ({
        postalCode: group.postalCode,
        city: group.city || t('common.unknownCity'),
        count: group.symptomCount || group.count || 0,
        totalReports: group.totalCount || group.count || 0
      }));
    } else {
      const totalSymptomCount = apiData.data.reduce((sum, group) => sum + (group.symptomCount || group.count || 0), 0);
      const totalReports = apiData.data.reduce((sum, group) => sum + (group.totalCount || group.count || 0), 0);
      
      return [{
        postalCode: level === 'city' ? t('common.multiplePostalCodes') : t('symptomCheck.results.nationwide'),
        city: level === 'city' ? apiData.data[0]?.city || t('common.unknownCity') : t('common.sweden'),
        count: totalSymptomCount,
        totalReports: totalReports
      }];
    }
  };

  const calculateTotalReports = (apiData) => {
    if (!apiData?.success || !apiData.data) return 0;
    return apiData.data.reduce((sum, group) => sum + (group.totalCount || group.count || 0), 0);
  };

  const fetchCityTrend = async (city, symptoms) => {
    try {
      const symptomsParam = symptoms.join(',');
      const currentWeek = selectedWeek;
      const previousWeek = selectedWeek - 1;
      
      const url = `${API_CONFIG.baseUrl}/health-data/trend?startWeek=${previousWeek}&endWeek=${currentWeek}&symptoms=${encodeURIComponent(symptomsParam)}&scope=city&city=${encodeURIComponent(city)}`;
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success && result.data && result.data.length >= 2) {
        const prevWeekData = result.data.find(d => d.week === previousWeek);
        const currWeekData = result.data.find(d => d.week === currentWeek);
        
        if (prevWeekData && currWeekData && prevWeekData.count > 0) {
          const changePercent = Math.round(((currWeekData.count - prevWeekData.count) / prevWeekData.count) * 100);
          const direction = changePercent > 0 ? 'up' : changePercent < 0 ? 'down' : 'stable';
          
          setCityTrend({
            changePercent: Math.abs(changePercent),
            direction: direction,
            previousCount: prevWeekData.count,
            currentCount: currWeekData.count
          });
        }
      }
    } catch (error) {
      console.error('❌ Error fetching city trend:', error);
      setCityTrend(null);
    }
  };

  const searchSimilarReports = async (dataToUse = null) => {
    const dataSource = dataToUse || submittedData;
    
    if (!dataSource) {
      return;
    }
    
    try {
      const { symptoms, postalCode, city } = dataSource;
      const normalizedPostal = normalizePostalCode(postalCode);
      const symptomNames = symptoms.map(s =>
        typeof s === 'string' ? s : s.name || s._id
      );

      const [postal, cityRes, country] = await Promise.all([
        fetchPostalGroups({ week: selectedWeek, symptoms: symptomNames, postalCode: normalizedPostal }),
        fetchPostalGroups({ week: selectedWeek, symptoms: symptomNames, city }),
        fetchPostalGroups({ week: selectedWeek, symptoms: symptomNames })
      ]);

      const transformedResults = {
        postal: {
          matches: processLevelMatches(postal, 'postal'),
          totalReports: calculateTotalReports(postal)
        },
        city: {
          matches: processLevelMatches(cityRes, 'city'),
          totalReports: calculateTotalReports(cityRes)
        },
        country: {
          matches: processLevelMatches(country, 'country'),
          totalReports: calculateTotalReports(country)
        }
      };

      setSearchResults(transformedResults);
      
      if (city && symptoms.length > 0) {
        fetchCityTrend(city, symptoms);
      }
      
    } catch (error) {
      console.error('❌ Error in community search:', error);
      setSearchResults({
        postal: { matches: [], totalReports: 0 },
        city: { matches: [], totalReports: 0 },
        country: { matches: [], totalReports: 0 }
      });
    }
  };

  const calculateMockRecoveryEstimate = (symptoms, severity) => {
    const baselineDays = {
      1: 3,
      2: 5,
      3: 7,
      4: 9,
      5: 11
    };
    
    const symptomCount = symptoms.length;
    let baseDays = baselineDays[Math.min(symptomCount, 5)] || 12;
    
    const severityMultiplier = {
      'low': 0.7,
      'medium': 1.0,
      'high': 1.4
    };
    
    baseDays = Math.round(baseDays * (severityMultiplier[severity] || 1.0));
    
    const variation = Math.random() > 0.5 ? 1 : -1;
    const estimatedDays = Math.max(2, baseDays + variation);
    
    const mockSampleSize = Math.floor(Math.random() * 200) + 50;
    const confidence = Math.min(95, 60 + (mockSampleSize / 5));
    
    return {
      estimatedDays: estimatedDays,
      daysRange: `${estimatedDays - 1}-${estimatedDays + 2}`,
      confidence: Math.round(confidence),
      sampleSize: mockSampleSize,
      isMockData: true
    };
  };

  const fetchDetailedInsights = async (dataToUse = null) => {
    const dataSource = dataToUse || submittedData;
    
    if (!dataSource || !dataSource.city) {
      return;
    }
    
    setDetailedInsights(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const { symptoms, severity, city } = dataSource;
      
      const [growingRes, popularRes] = await Promise.all([
        fetch(`${API_CONFIG.baseUrl}/health-data/symptom-analysis?type=growing&weeks=51,52,53&city=${encodeURIComponent(city)}`)
          .then(r => r.json())
          .catch(err => {
            console.error('❌ Growing symptoms error:', err);
            return { success: false };
          }),
        
        fetch(`${API_CONFIG.baseUrl}/health-data/symptom-analysis?type=popular&week=${selectedWeek}&city=${encodeURIComponent(city)}`)
          .then(r => r.json())
          .catch(err => {
            console.error('❌ Popular symptoms error:', err);
            return { success: false };
          })
      ]);
      
      const recoveryEstimate = calculateMockRecoveryEstimate(symptoms, severity);
      
      setDetailedInsights({
        growing: growingRes.success ? growingRes.data : null,
        popular: popularRes.success ? popularRes.data : null,
        recovery: recoveryEstimate,
        loading: false,
        error: null
      });
      
    } catch (error) {
      console.error('❌ Error fetching detailed insights:', error);
      setDetailedInsights(prev => ({
        ...prev,
        loading: false,
        error: t('symptomCheck.insights.fetchError')
      }));
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailAddress || !submittedData) {
      setMessage({
        type: 'error',
        text: t('symptomCheck.recovery.emailRequired')
      });
      return;
    }

    try {
      const userData = {
        email: emailAddress,
        accountStatus: 'email_only',
        healthData: {
          anonymousReports: [{
            symptoms: submittedData.symptoms,
            severity: submittedData.severity,
            postalCode: submittedData.postalCode,
            city: submittedData.city,
            notes: submittedData.notes,
            reportDate: submittedData.timestamp,
            week: submittedData.week,
            // ✅ Backend will generate recoveryCode
            reminderScheduled: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            reminderSent: false
          }]
        },
        createdAt: new Date().toISOString()
      };

      console.log('📧 Submitting email reminder request...', userData);

      const response = await fetch(`${API_CONFIG.baseUrl}/users/email-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const result = await response.json();
      
      console.log('📧 Email reminder response:', result);

      if (result.success) {
        setEmailSubmitted(true);
        
        // ✅ NEW: Set recovery code from backend response
        if (result.recoveryCode) {
          setRecoveryCode(result.recoveryCode);
          console.log(`✅ Recovery code received from backend: ${result.recoveryCode}`);
        }
        
        setMessage({
          type: 'success',
          text: t('symptomCheck.recovery.emailSuccess')
        });
      } else {
        throw new Error(result.message || 'Failed to save email reminder');
      }

    } catch (error) {
      console.error('❌ Error saving email reminder:', error);
      setMessage({
        type: 'error',
        text: t('symptomCheck.recovery.emailError')
      });
    }
  };

  const DetailedInsightsSection = () => {
    if (!showResults || !submittedData) return null;
    
    return (
      <div className="detailed-insights-section">
        
        <div className="insight-card-detailed growing-symptoms">
          <div className="insight-header">
            <h4>{t('symptomCheck.insights.growingTitle')}</h4>
            <span className="insight-location">{t('symptomCheck.insights.inLocation', { location: submittedData.city })}</span>
          </div>
          
          {detailedInsights.loading ? (
            <div className="insight-loading">
              <div className="loading-spinner"></div>
              <span>{t('symptomCheck.insights.fetchingData')}</span>
            </div>
          ) : detailedInsights.growing?.symptoms ? (
            <div className="symptom-list">
              {detailedInsights.growing.symptoms.slice(0, 3).map((symptom, idx) => (
                <div key={idx} className="symptom-item">
                  <span className="symptom-rank">#{idx + 1}</span>
                  <span className="symptom-name">{tSymptom(symptom.name)}</span>
                  <span className="symptom-growth">↑+{symptom.growth}%</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="insight-empty">
              <span>{t('symptomCheck.insights.noGrowthData')}</span>
            </div>
          )}
          
          <div className="insight-footer">
            <small>{t('symptomCheck.insights.lastWeeks')}</small>
          </div>
        </div>
        
        <div className="insight-card-detailed popular-symptoms">
          <div className="insight-header">
            <h4>{t('symptomCheck.insights.popularTitle')}</h4>
            <span className="insight-location">{t('symptomCheck.insights.inLocation', { location: submittedData.city })}</span>
          </div>
          
          {detailedInsights.loading ? (
            <div className="insight-loading">
              <div className="loading-spinner"></div>
              <span>{t('symptomCheck.insights.fetchingData')}</span>
            </div>
          ) : detailedInsights.popular?.symptoms ? (
            <div className="symptom-list">
              {detailedInsights.popular.symptoms.slice(0, 3).map((symptom, idx) => (
                <div key={idx} className="symptom-item">
                  <span className="symptom-rank">#{idx + 1}</span>
                  <span className="symptom-name">{tSymptom(symptom.name)}</span>
                  <span className="symptom-count">{symptom.count}p</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="insight-empty">
              <span>{t('symptomCheck.insights.noSymptomData')}</span>
            </div>
          )}
          
          <div className="insight-footer">
            <small>{t('symptomCheck.insights.week', { week: selectedWeek })}</small>
          </div>
        </div>
        
        <div className="insight-card-detailed recovery-time">
          <div className="insight-header">
            <h4>{t('symptomCheck.insights.recoveryTitle')}</h4>
            <span className="insight-location">
              {submittedData.symptoms.map(s => tSymptom(s)).join(' + ')}
            </span>
          </div>
          
          {detailedInsights.recovery ? (
            <div className="recovery-estimate">
              <div className="days-estimate">
                <span className="days-number">{detailedInsights.recovery.daysRange}</span>
                <span className="days-label">{t('symptomCheck.insights.days')}</span>
              </div>
              
              <div className="confidence-bar-wrapper">
                <div className="confidence-label">
                  {t('symptomCheck.insights.confidence', { percent: detailedInsights.recovery.confidence })}
                </div>
                <div className="confidence-bar-track">
                  <div 
                    className="confidence-bar-fill"
                    style={{ width: `${detailedInsights.recovery.confidence}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="insight-loading">
              <span>{t('symptomCheck.insights.calculatingEstimate')}</span>
            </div>
          )}
          
          <div className="insight-footer">
            <small>
              {t('symptomCheck.insights.basedOnCases', { count: detailedInsights.recovery?.sampleSize || 0 })}
              {detailedInsights.recovery?.isMockData && process.env.NODE_ENV === 'development' && (
                <span className="mock-badge"> (Mock Data)</span>
              )}
            </small>
          </div>
        </div>
        
      </div>
    );
  };

  return (
    <div className={`user-reports-page ${submitSuccess ? 'report-submitted' : ''}`}>
      <div className="page-header">
        <h1>
          <span className="header-title">{t('symptomCheck.pageTitle')}</span>
          {submitSuccess && submittedData && (
            <span className="header-details">
              <span className="header-symptoms">{submittedData.symptoms.map(s => tSymptom(s)).join(', ')}</span>
              <span className="header-location">📍 {submittedData.city}, {submittedData.postalCode}</span>
            </span>
          )}
        </h1>
      </div>
      
      {message && message.type === 'error' && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
      
      <div className="main-content">
        {showResults && submitSuccess ? (
          <div className="results-view">
            
            <div className="results-content">
              <div className="community-insights-section">
                
                <div className="insights-grid">
                  <div className="insight-card">
                    <div className="insight-icon">📍</div>
                    <div className="insight-content">
                      <div className="insight-title">{t('symptomCheck.results.yourArea')}</div>
                      <div className="insight-value">
                        {searchResults?.postal?.matches?.[0]?.count || 0}/
                        {searchResults?.postal?.matches?.[0]?.totalReports || 0}
                      </div>
                      <div className="insight-description">
                        {t('symptomCheck.results.exactMatch')}
                      </div>
                    </div>
                  </div>
                  
                  <div className="insight-card">
                    <div className="insight-icon">🏙️</div>
                    <div className="insight-content">
                      <div className="insight-title">{submittedData?.city || t('symptomCheck.results.yourCity')}</div>
                      
                      {cityTrend ? (
                        <>
                          <div className={`insight-value trend-${cityTrend.direction}`}>
                            {cityTrend.direction === 'up' ? '↑' : cityTrend.direction === 'down' ? '↓' : '→'} 
                            {cityTrend.changePercent}%
                          </div>
                          <div className="insight-subtitle">
                            {t('symptomCheck.results.vsLastWeek')}
                          </div>
                          <div className="insight-description" style={{ fontSize: '0.75em', marginTop: '4px' }}>
                            {searchResults?.city?.matches?.[0]?.count || 0}/
                            {searchResults?.city?.matches?.[0]?.totalReports || 0} {t('symptomCheck.results.reports')}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="insight-value">
                            {searchResults?.city?.matches?.[0]?.count || 0}/
                            {searchResults?.city?.matches?.[0]?.totalReports || 0}
                          </div>
                          <div className="insight-description">
                            {t('symptomCheck.results.cityPatterns')}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="insight-card">
                    <div className="insight-content">
                      <div className="insight-title">{t('symptomCheck.results.nationwide')}</div>
                      
                      {submittedData?.symptoms?.length > 0 ? (
                        <div style={{ marginTop: '8px' }}>
                          <QuarterlyTrendChart 
                            symptoms={submittedData.symptoms}
                            level="country"
                          />
                        </div>
                      ) : (
                        <>
                          <div className="insight-value">
                            {searchResults?.country?.matches?.[0]?.count || 0}/
                            {searchResults?.country?.matches?.[0]?.totalReports || 0}
                          </div>
                          <div className="insight-description">
                            {t('symptomCheck.results.nationalTrends')}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <DetailedInsightsSection />

              {/* ✅ Recovery code section - shows AFTER email submission */}
              {recoveryCode && (
                <div className="recovery-code-section">
                  <div className="recovery-code-box">
                    <div className="recovery-code-content-wrapper">
                      <div className="recovery-code-left">
                        <div className="recovery-icon">🔑</div>
                        <div className="recovery-content">
                          <div className="recovery-title">{t('symptomCheck.recovery.codeTitle')}</div>
                          <div className="recovery-code-display">{recoveryCode}</div>
                          <div className="recovery-subtitle">
                            {t('symptomCheck.recovery.emailSent', { email: emailAddress })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ✅ Email form - shows BEFORE recovery code */}
              {!recoveryCode && !emailSubmitted && (
                <div className="recovery-code-section">
                  <div className="recovery-code-box">
                    <div className="recovery-code-content-wrapper">
                      <div className="recovery-code-right">
                        <h4>{t('symptomCheck.recovery.emailFormTitle')}</h4>
                        <p>{t('symptomCheck.recovery.emailFormDescription')}</p>
                        
                        <form onSubmit={handleEmailSubmit} className="email-form">
                          <div className="email-input-group">
                            <input
                              type="email"
                              value={emailAddress}
                              onChange={(e) => setEmailAddress(e.target.value)}
                              placeholder={t('symptomCheck.recovery.emailPlaceholder')}
                              required
                            />
                            <button type="submit" disabled={!emailAddress}>
                              {t('symptomCheck.recovery.sendCode')}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        ) : (
          <>
            <div className="form-container">
              <form onSubmit={handleSubmit} className="symptom-form">
                <div className="form-group">
                  <label>{t('symptomCheck.form.postalCodeRequired')}</label>
                  <PostalCodeInput
                    label={null} 
                    value={reportData.postalCode}
                    onChange={handlePostalCodeChange}
                    onSelect={handlePostalCodeSelect}
                    onValidationChange={handlePostalCodeValidation}
                    suggestions={postalCodeSuggestions}
                    placeholder={t('symptomCheck.form.postalCodePlaceholder')}
                  />
                  {reportData.city && (
                    <div className="detected-city">{t('symptomCheck.form.cityDetected', { city: reportData.city })}</div>
                  )}
                </div>
                
                <div className="form-group">
                  <label>{t('symptomCheck.form.symptomsRequired')}</label>
                  <div className="symptoms-grid">
                    {ALL_SYMPTOMS.map((symptom) => {
                      const symptomName = typeof symptom === 'string' ? symptom : symptom.name || symptom._id;
                      const isSelected = isSymptomSelected(symptom);
                      
                      return (
                        <button
                          key={symptomName}
                          type="button"
                          className={`symptom-button ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleSymptomToggle(symptom)}
                          style={{
                            '--symptom-color': getSymptomColor(symptomName)
                          }}
                        >
                          {tSymptom(symptomName)}
                        </button>
                      );
                    })}
                  </div>
                  {reportData.symptoms.length > 0 && (
                    <div className="selected-symptoms">
                      {t('symptomCheck.form.symptomsSelected', { symptoms: reportData.symptoms.map(s => tSymptom(s)).join(', ') })}
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label>{t('symptomCheck.form.severity')}</label>
                  <select
                    name="severity"
                    value={reportData.severity}
                    onChange={handleInputChange}
                  >
                    <option value="low">{t('symptomCheck.form.severityLow')}</option>
                    <option value="medium">{t('symptomCheck.form.severityMedium')}</option>
                    <option value="high">{t('symptomCheck.form.severityHigh')}</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>{t('symptomCheck.form.notes')}</label>
                  <textarea
                    name="notes"
                    value={reportData.notes}
                    onChange={handleInputChange}
                    placeholder={t('symptomCheck.form.notesPlaceholder')}
                    rows="3"
                  />
                </div>
                
                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading || !reportData.symptoms.length || !postalCodeValid}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      {t('symptomCheck.form.submitting')}
                    </>
                  ) : (
                    <>
                      <span>📊</span>
                      {t('symptomCheck.form.submit')}
                    </>
                  )}
                </button>
              </form>
            </div>
            
            <div className="results-box">
              <div className="results-placeholder">
                <div className="results-placeholder-icon">
                  <span role="img" aria-label="Rapport">📊</span>
                </div>
                <p className="results-placeholder-text">
                  {t('symptomCheck.placeholder.title')}
                  <br /><br />
                  <strong>{t('symptomCheck.placeholder.yourArea')}</strong> {t('symptomCheck.placeholder.yourAreaDesc')}
                  <br /><strong>{t('symptomCheck.placeholder.trends')}</strong> {t('symptomCheck.placeholder.trendsDesc')}
                  <br /><strong>{t('symptomCheck.placeholder.growing')}</strong> {t('symptomCheck.placeholder.growingDesc')}
                  <br /><strong>{t('symptomCheck.placeholder.duration')}</strong> {t('symptomCheck.placeholder.durationDesc')}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserReports;
